import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Boxes, Loader2, X } from "lucide-react";
import Field from "@/shared/components/field";
import type { WorkspaceSummary, WorkspaceUpsert } from "../models/Workspace";

const PRESET_COLORS = [
  "#0071e3",
  "#8b5cf6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#0ea5e9",
  "#ec4899",
  "#64748b",
];

export interface WorkspaceFormModalProps {
  /** Workspace a editar, o null para crear uno nuevo. */
  workspace: WorkspaceSummary | null;
  saving: boolean;
  onSave: (upsert: WorkspaceUpsert) => void;
  onClose: () => void;
}

export default function WorkspaceFormModal({
  workspace,
  saving,
  onSave,
  onClose,
}: WorkspaceFormModalProps) {
  const [name, setName] = useState(workspace?.name ?? "");
  const [description, setDescription] = useState(workspace?.description ?? "");
  const [icon, setIcon] = useState(workspace?.icon ?? "");
  const [color, setColor] = useState(workspace?.color ?? "");

  const canSave = name.trim() !== "";

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/40 backdrop-blur-md"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          className="relative z-10 flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-3xl border border-zinc-200/50 bg-white/90 shadow-2xl backdrop-blur-2xl"
        >
          <div className="flex items-start gap-4 border-b border-black/5 p-6">
            <div className="shrink-0 rounded-2xl bg-accent/10 p-3">
              <Boxes className="size-6 text-accent" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-bold text-foreground">
                {workspace ? "Editar workspace" : "Nuevo workspace"}
              </h3>
              <p className="mt-0.5 text-sm text-foreground/50">
                Agrupá las automatizaciones de una misma aplicación.
              </p>
            </div>
            <button
              onClick={onClose}
              title="Cerrar"
              className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-black/5 bg-white/50 text-foreground/40 transition-all hover:bg-white hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto p-6">
            <Field
              label="Nombre"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Portal de clientes"
              required
            />
            <Field
              label="Descripción"
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Qué agrupa este workspace"
            />
            <Field
              label="Ícono (emoji o URL)"
              name="icon"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="📦"
            />

            <div className="space-y-2">
              <span className="ml-1 text-sm font-bold uppercase tracking-wider text-foreground/60">
                Color
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setColor("")}
                  title="Sin color"
                  className={`size-8 cursor-pointer rounded-xl border-2 bg-white transition-all ${
                    color === "" ? "border-accent shadow-sm" : "border-black/5 hover:border-black/20"
                  }`}
                />
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    title={c}
                    style={{ backgroundColor: c }}
                    className={`size-8 cursor-pointer rounded-xl border-2 transition-all ${
                      color === c ? "border-foreground shadow-sm" : "border-transparent hover:scale-110"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-black/5 p-5">
            <button
              onClick={onClose}
              className="cursor-pointer rounded-xl border border-black/5 bg-white/60 px-5 py-2 text-sm font-semibold text-foreground/70 transition-all hover:bg-white"
            >
              Cancelar
            </button>
            <button
              onClick={() =>
                onSave({
                  name: name.trim(),
                  description: description.trim(),
                  icon: icon.trim() || null,
                  color: color || null,
                })
              }
              disabled={!canSave || saving}
              className="flex cursor-pointer items-center gap-2 rounded-xl bg-accent px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-accent/25 transition-all hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving && <Loader2 className="size-4 animate-spin" />}
              {workspace ? "Guardar cambios" : "Crear workspace"}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
